import React, { useEffect, useState } from 'react';
import { BarChart3, ClipboardList, Layers3, Users, X } from 'lucide-react';

const TABS = [
  { id: 'summary', label: 'Summary', icon: BarChart3 },
  { id: 'segments', label: 'Segments', icon: ClipboardList },
  { id: 'lenses', label: 'Lenses', icon: Layers3 },
  { id: 'playerCards', label: 'Player Cards', icon: Users },
];

const LENS_KEYS = [
  'offensive',
  'defensive',
  'performance',
  'tactical',
  'biomechanics',
  'shooting',
];

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function pickFirstArray(...values) {
  for (const value of values) {
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
  }

  for (const value of values) {
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
}

function getSegments(pipelineResponse, reportSummary) {
  return pickFirstArray(
    pipelineResponse?.segments,
    reportSummary?.segments,
    reportSummary?.top_segments,
    reportSummary?.key_plays
  );
}

function getPlayerCards(pipelineResponse, reportSummary) {
  return pickFirstArray(
    pipelineResponse?.player_cards,
    reportSummary?.player_cards,
    reportSummary?.players
  );
}

function getLensSource(pipelineResponse, reportSummary) {
  return (
    pipelineResponse?.lenses ||
    reportSummary?.lenses ||
    reportSummary?.lens_scores ||
    reportSummary?.lens_breakdown ||
    {}
  );
}

function findLensData(rawLenses, lensKey) {
  if (Array.isArray(rawLenses)) {
    return (
      rawLenses.find((entry) => {
        const id = String(entry?.id || entry?.lens || entry?.name || '').toLowerCase();
        return id === lensKey;
      }) || null
    );
  }

  return rawLenses?.[lensKey] || rawLenses?.[lensKey.toUpperCase()] || null;
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return '--';
  }

  if (typeof value === 'number') {
    return value.toFixed(1);
  }

  return String(value);
}

export default function AnalysisOverlay({ pipelineResponse, gameName, onClose }) {
  const [activeTab, setActiveTab] = useState('summary');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setActiveTab('summary');
    setMounted(false);
    const timeoutId = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(timeoutId);
  }, [pipelineResponse, gameName]);

  const reportSummary = pipelineResponse?.report_summary || {};
  const segments = getSegments(pipelineResponse, reportSummary);
  const playerCards = getPlayerCards(pipelineResponse, reportSummary);
  const rawLenses = getLensSource(pipelineResponse, reportSummary);
  const lensRows = LENS_KEYS.map((lensKey) => {
    const data = findLensData(rawLenses, lensKey);
    const executionScore =
      toNumber(data?.execution_score) ??
      toNumber(data?.executionScore) ??
      toNumber(data?.execution) ??
      toNumber(data?.score);
    const tacticalScore =
      toNumber(data?.tactical_score) ??
      toNumber(data?.tacticalScore) ??
      toNumber(data?.tactical) ??
      null;

    return {
      id: lensKey,
      label: lensKey.replace(/(^\w|-\w)/g, (match) => match.replace('-', '').toUpperCase()),
      executionScore,
      tacticalScore,
      summary: data?.summary || data?.description || data?.notes || '',
    };
  });

  return (
    <div
      className={[
        'overflow-hidden rounded-3xl border border-[#1A1A1A] bg-[#0A0A0A]/95 shadow-[0_24px_80px_rgba(0,0,0,0.45)] transition-all duration-300',
        mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0',
      ].join(' ')}
    >
      <div className="border-b border-[#1A1A1A] bg-gradient-to-r from-[#111111] via-[#0A0A0A] to-[#111111] p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-zinc-500">
              Analysis Complete
            </div>
            <div className="mt-1 text-2xl font-semibold text-white">
              {gameName || pipelineResponse?.game_name || 'Untitled Game'}
            </div>
            <div className="mt-2 text-sm text-zinc-400">
              Review segments, six-lens scoring, and player card outputs from the pipeline response.
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-[#1A1A1A] bg-[#111111] p-2 text-zinc-400 transition-colors hover:border-[#E8B800] hover:text-white"
            aria-label="Close analysis overlay"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={[
                  'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors',
                  isActive
                    ? 'border-[#E8B800] bg-[#E8B800]/10 text-[#E8B800]'
                    : 'border-[#1A1A1A] bg-[#111111] text-zinc-400 hover:text-white',
                ].join(' ')}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-5">
        {activeTab === 'summary' ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-[#1A1A1A] bg-[#111111] p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Game</div>
              <div className="mt-2 text-lg font-semibold text-white">
                {gameName || pipelineResponse?.game_name || 'Untitled Game'}
              </div>
            </div>
            <div className="rounded-2xl border border-[#1A1A1A] bg-[#111111] p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Total Segments</div>
              <div className="mt-2 text-3xl font-semibold text-white">
                {pipelineResponse?.total_segments ?? segments.length ?? 0}
              </div>
            </div>
            <div className="rounded-2xl border border-[#1A1A1A] bg-[#111111] p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Total Clips</div>
              <div className="mt-2 text-3xl font-semibold text-white">
                {pipelineResponse?.total_clips ?? reportSummary?.total_clips ?? 0}
              </div>
            </div>
            <div className="rounded-2xl border border-[#1A1A1A] bg-[#111111] p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Total Analyses</div>
              <div className="mt-2 text-3xl font-semibold text-white">
                {pipelineResponse?.total_analyses ?? reportSummary?.total_analyses ?? 0}
              </div>
            </div>
            <div className="rounded-2xl border border-[#1A1A1A] bg-[#111111] p-4 md:col-span-2 xl:col-span-4">
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Report Summary</div>
              <div className="mt-3 text-sm leading-6 text-zinc-300">
                {reportSummary?.summary ||
                  reportSummary?.overview ||
                  reportSummary?.headline ||
                  'The pipeline completed successfully, but no written summary was returned in the payload.'}
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === 'segments' ? (
          <div className="space-y-3">
            {segments.length ? (
              segments.map((segment, index) => (
                <div
                  key={segment.segment_id || segment.id || `${segment.play_type || 'segment'}-${index}`}
                  className="rounded-2xl border border-[#1A1A1A] bg-[#111111] p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white">
                        {segment.play_type || segment.playType || `Segment ${index + 1}`}
                      </div>
                      <div className="mt-1 text-sm text-zinc-400">
                        {segment.description || segment.summary || 'No segment description available.'}
                      </div>
                    </div>
                    <div className="rounded-full border border-[#1A1A1A] bg-[#0A0A0A] px-3 py-1 text-xs text-zinc-400">
                      {segment.timestamps ||
                        segment.timestamp ||
                        segment.time_range ||
                        `${segment.start_time || segment.startTime || '--'} - ${segment.end_time || segment.endTime || '--'}`}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-[#1A1A1A] bg-[#050505] p-6 text-sm text-zinc-500">
                No segment-level detail was included in this pipeline response.
              </div>
            )}
          </div>
        ) : null}

        {activeTab === 'lenses' ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {lensRows.map((lens) => (
              <div key={lens.id} className="rounded-2xl border border-[#1A1A1A] bg-[#111111] p-4">
                <div className="text-sm font-semibold text-white">{lens.label}</div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-[#1A1A1A] bg-[#0A0A0A] p-3">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Execution</div>
                    <div className="mt-2 text-2xl font-semibold text-[#E8B800]">
                      {formatValue(lens.executionScore)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-[#1A1A1A] bg-[#0A0A0A] p-3">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Tactical</div>
                    <div className="mt-2 text-2xl font-semibold text-white">
                      {formatValue(lens.tacticalScore)}
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-sm leading-6 text-zinc-400">
                  {lens.summary || 'No written insight returned for this lens.'}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {activeTab === 'playerCards' ? (
          <div className="space-y-3">
            {playerCards.length ? (
              playerCards.map((player, index) => (
                <div
                  key={player.player_id || player.id || player.player_name || player.name || index}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-[#1A1A1A] bg-[#111111] p-4"
                >
                  <div>
                    <div className="text-sm font-semibold text-white">
                      {player.player_name || player.playerName || player.name || `Player ${index + 1}`}
                    </div>
                    <div className="mt-1 text-sm text-zinc-500">
                      {player.position || player.role || 'Composite evaluation available'}
                    </div>
                  </div>
                  <div className="rounded-full border border-[#1A1A1A] bg-[#0A0A0A] px-4 py-2 text-sm font-semibold text-[#E8B800]">
                    Grade {formatValue(player.composite_grade ?? player.compositeGrade ?? player.grade)}
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-[#1A1A1A] bg-[#050505] p-6 text-sm text-zinc-500">
                No player cards were included in this response.
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
